import { useMemo } from "react";
import { ButtonSize, IconSize } from "../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../classes/enums/IconsEnum";
import {
  ButtonStateEnum,
  ColorClassEnum,
  DISPLAY_ONLY_PARAM_NATURES,
  TargetTypeEnum,
  getBaseTargetType,
} from "../../../classes/enums/SppEnum";
import { roundToUnit } from "../../../classes/utils/NumbersUtils";
import ColoredRangeSlider from "../../../components/coloredRangeSlider/ColoredRangeSlider";
import IconButton from "../../../components/iconButton/IconButton";
import Tooltip, {
  TooltipOrientation,
} from "../../../components/tooltip/Tooltip";
import "./Spp.css";

const SppParameter = ({
  param,
  onSppSelected,
  isVisible,
  isOctView,
  onToggleFavorite,
  favoriteParams,
  selectedTargetType,
  allowFavorites = false,
  isSettingsMode = false,
  compareMode = false,
  currentPlanLabel = "Plan",
  comparedPlanLabel = "Plan",
  disabled = false,
}) => {
  
  if (param.shortName === 'LL' || param.shortName === 'PI' || param.shortName === 'PT') {
  }

  const isDisplayOnly =
    DISPLAY_ONLY_PARAM_NATURES.includes(param.nature) ||
    param.pythonClass === "" ||
    param.originalValue === false;

  const isGaporSaasScore =
    param.shortName === "GAP_score" || param.shortName === "SAAS_score";

  const getColorForValue = (value, ranges) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return ColorClassEnum.TRANSPARENT;

    for (let greenRange of ranges.green || []) {
      if (numValue >= greenRange[0] && numValue <= greenRange[1]) {
        return ColorClassEnum.GREEN;
      }
    }
    for (let yellowRange of ranges.yellow || []) {
      if (numValue >= yellowRange[0] && numValue <= yellowRange[1]) {
        return ColorClassEnum.YELLOW;
      }
    }

    if ((ranges.red || []).length > 0) {
      return ColorClassEnum.RED;
    }

    return ColorClassEnum.TRANSPARENT;
  };

  const renderValue = (value, defaultValue = "-", isTarget = false) => {
    if (value === undefined || value === null) {
      return <span className="Spp-measurement-value">{defaultValue}</span>;
    }

    if (isTarget || isDisplayOnly) {
      return (
        <span className="Spp-measurement-value">{roundToUnit(value)}</span>
      );
    }

    const ranges = {
      green: param.greenRegions || [],
      yellow: param.yellowRegions || [],
      red: param.redRegions || [],
    };

    return (
      <span
        className={`Spp-measurement-value`} //<- this is for setting color range ${getColorForValue(value, ranges)}
      >
        {roundToUnit(value)}
      </span>
    );
  };

  const getMinMax = () => {
    const yellowRegions = param.yellowRegions || [];
    const greenRegions = param.greenRegions || [];
    const redRegions = param.redRegions || [];
    const allRanges = [...yellowRegions, ...greenRegions, ...redRegions];

    const calculateStandardDeviation = values => {
      if (values.length === 0) return 0;
      const mean = values.reduce((a, b) => a + b) / values.length;
      const squaredDifferences = values.map(x => Math.pow(x - mean, 2));
      const variance =
        squaredDifferences.reduce((a, b) => a + b) / values.length;
      return Math.sqrt(variance);
    };

    const allValues = allRanges.flat();

    if (allValues.length === 0) {
      return { min: 0, max: 100 }; 
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    const stdDev = calculateStandardDeviation(allValues);

    // Only floor at 0 for non-negative scales (GAP). Scores centred on 0
    // (SAAS) need the negative half kept, otherwise their mirrored yellow
    // and red bands fall outside the slider domain.
    const extendedMin = min >= 0 ? Math.max(min - stdDev, 0) : min - stdDev;
    const extendedMax = max + stdDev;

    return {
      min: extendedMin,
      max: extendedMax,
    };
  };

  const hasIntervals = useMemo(() => {
    const allRegions = [
      ...(param.greenRegions || []),
      ...(param.yellowRegions || []),
      ...(param.redRegions || []),
    ];

    return allRegions.some(
      interval =>
        Array.isArray(interval) &&
        interval.length === 2 &&
        Number.isFinite(interval[0]) &&
        Number.isFinite(interval[1]) &&
        interval[0] !== interval[1]
    );
  }, [param.greenRegions, param.yellowRegions, param.redRegions]);

  const { min, max } = getMinMax();
  // const shouldShowRange = Boolean(isVisible && hasIntervals);
  const shouldShowRange = isGaporSaasScore;

  const markers = useMemo(() => {
    const markersList = [];

    if (param.originalValue != null && !isNaN(param.originalValue)) {
      markersList.push({
        label: "PRE",
        value: param.originalValue,
        color: "#ffffffff",
      });
    }

    if (isOctView) {
      if (!compareMode) {
        if (param.targetValue != null && !isNaN(param.targetValue)) {
          const baseTargetType = getBaseTargetType(selectedTargetType);
          const targetLabel =
            baseTargetType === TargetTypeEnum.SAAS ? "SAAS" : "GAP";
          markersList.push({
            label: targetLabel,
            value: param.targetValue,
            color: "#ffffff",
          });
        }

        if (param.currentValue != null && !isNaN(param.currentValue)) {
          markersList.push({
            label: "PLAN",
            value: param.currentValue,
            color: "#FFFFFF",
          });
        }
      } else {
        if (param.currentValue != null && !isNaN(param.currentValue)) {
          markersList.push({
            label: currentPlanLabel?.toUpperCase(),
            value: param.currentValue,
            color: "#4DA3FF",
          });
        }

        if (
          param.comparedCurrentValue != null &&
          !isNaN(param.comparedCurrentValue)
        ) {
          markersList.push({
            label: comparedPlanLabel?.toUpperCase(),
            value: param.comparedCurrentValue,
            color: "#7EC8FF",
          });
        }
      }
    }

    return markersList;
  }, [
    isOctView,
    param.originalValue,
    param.targetValue,
    param.currentValue,
    param.comparedCurrentValue,
    selectedTargetType,
    compareMode,
    currentPlanLabel,
    comparedPlanLabel,
  ]);

  const hasNoValue = isOctView
    ? compareMode
      ? param.currentValue == null && param.comparedCurrentValue == null
      : param.currentValue == null
    : param.originalValue == null;

  const isClickDisabled = disabled || hasNoValue;

  const handleToggle = () => {
    if (disabled) return;
    if (allowFavorites && onToggleFavorite) {
      onToggleFavorite(param.key);
    }
  };

  const isFavorite = useMemo(() => {
    if (!allowFavorites || !favoriteParams) {
      return false;
    }

    const numericKey = Number(param.key);
    if (Number.isFinite(numericKey) && favoriteParams.has(numericKey)) {
      return true;
    }

    return favoriteParams.has(param.key);
  }, [allowFavorites, favoriteParams, param.key]);

  if (isSettingsMode) {
    return (
      <div className="spp-parameter spp-parameter-settings">
        <div className="Analyse-spp-option App-text-medium settings-spp-option">
          <span className="Spp-measurement-name">
            <Tooltip text={param.fullName} orientation={TooltipOrientation.Top}>
              {param.shortName}
              {param.unit ? ` [${param.unit}]` : ""}
            </Tooltip>
            <IconButton
              icon="Info"
              title={
                param.description || param.fullName || param.name || param.key
              }
              className="spp-info-button"
              onClick={e => e.stopPropagation()}
              isCircle
              size={ButtonSize.MICRO}
              isLightBlue={false}
              tooltipOrientation={TooltipOrientation.Right}
            />
            {allowFavorites && (
              <IconButton
                icon="Favorite"
                title={
                  isFavorite ? "Remove from favorites" : "Add to favorites"
                }
                className="spp-info-button"
                onClick={e => e.stopPropagation()}
                isCircle
                size={ButtonSize.MICRO}
                iconSize={IconSize.SMALL}
                isLightBlue={false}
                isTogglable
                isToggled={isFavorite}
                onToggle={handleToggle}
                tooltipOrientation={TooltipOrientation.Right}
              />
            )}
          </span>
        </div>
      </div>
    );
  }


  if (isGaporSaasScore) {
    return (
      <div className="spp-parameter">
        <div
          className="Analyse-spp-option App-text-medium"
          onClick={() => {
            if (!isClickDisabled) onSppSelected(param);
          }}
          style={isClickDisabled ? { pointerEvents: "none", opacity: hasNoValue ? 0.4 : 0.6 } : undefined}
        >
          <span className="Spp-measurement-name">
            <Tooltip text={param.fullName} orientation={TooltipOrientation.Top}>
              {param.shortName}
              {param.unit ? ` [${param.unit}]` : ""}
            </Tooltip>
            <IconButton
              icon="Info"
              title={
                param.description || param.fullName || param.name || param.key
              }
              className="spp-info-button"
              onClick={e => e.stopPropagation()}
              isCircle
              size={ButtonSize.MICRO}
              isLightBlue={false}
              tooltipOrientation={TooltipOrientation.Right}
              disabled={disabled}
            />
            {allowFavorites && (
              <IconButton
                icon="Favorite"
                title={
                  isFavorite ? "Remove from favorites" : "Add to favorites"
                }
                className="spp-info-button"
                onClick={e => e.stopPropagation()}
                isCircle
                size={ButtonSize.MICRO}
                iconSize={IconSize.SMALL}
                isLightBlue={false}
                isTogglable
                isToggled={isFavorite}
                onToggle={handleToggle}
                tooltipOrientation={TooltipOrientation.Right}
                disabled={disabled}
              />
            )}
          </span>

          <span
            className={`Spp-measurement-values-container ${
              isOctView ? "oct-view" : ""
            } ${compareMode ? "compare-mode" : ""}`}
          >
            {isOctView && compareMode ? (
              <>
                {renderValue(param.currentValue, param.defaultValue)}
                {renderValue(param.comparedCurrentValue, param.defaultValue)}
                {renderValue(param.originalValue, param.defaultValue)}
              </>
            ) : (
              <>
                {isOctView &&
                  renderValue(param.targetValue, param.defaultValue, true)}
                {isOctView && renderValue(param.currentValue, param.defaultValue)}
                {renderValue(param.originalValue, param.defaultValue)}
              </>
            )}
          </span>
        </div>
        <div className="Analyse-range-slider-container">
          {shouldShowRange && (
            <ColoredRangeSlider
              min={min}
              max={max}
              value={param.originalValue}
              onValueChange={() => {}}
              mode="gradient"
              ranges={{
                yellow: param.yellowRegions || [],
                green: param.greenRegions || [],
                red: param.redRegions || [],
              }}
              markers={markers}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="spp-parameter">
      <div
        className="Analyse-spp-option App-text-medium"
        onClick={() => {
          if (!isClickDisabled) onSppSelected(param);
        }}
        style={isClickDisabled ? { pointerEvents: "none", opacity: hasNoValue ? 0.4 : 0.6 } : undefined}
      >
        <div
          className={`App-option-circle ${
            isVisible ? ButtonStateEnum.ACTIVE : ""
          } ${isDisplayOnly ? "display-only" : ""}`}
        ></div>
        <span className="Spp-measurement-name">
          <Tooltip text={param.fullName} orientation={TooltipOrientation.Top}>
            {param.shortName}
            {param.unit ? ` [${param.unit}]` : ""}
          </Tooltip>
          <IconButton
            icon={IconsEnum.INFO}
            title={
              param.description || param.fullName || param.name || param.key
            }
            className="spp-info-button"
            onClick={e => e.stopPropagation()}
            isCircle
            size={ButtonSize.MICRO}
            isLightBlue={false}
            tooltipOrientation={TooltipOrientation.Right}
            disabled={disabled}
          />
          {allowFavorites && (
            <IconButton
              icon="Favorite"
              title={isFavorite ? "Remove from favorites" : "Add to favorites"}
              className="spp-info-button"
              onClick={e => e.stopPropagation()}
              isCircle
              size={ButtonSize.MICRO}
              iconSize={IconSize.SMALL}
              isLightBlue={false}
              isTogglable
              isToggled={isFavorite}
              onToggle={handleToggle}
              tooltipOrientation={TooltipOrientation.Right}
              disabled={disabled}
            />
          )}
        </span>

        <span
          className={`Spp-measurement-values-container ${
            isOctView ? "oct-view" : ""
          } ${compareMode ? "compare-mode" : ""}`}
        >
          {isOctView && compareMode ? (
            <>
              {renderValue(param.currentValue, param.defaultValue)}
              {renderValue(param.comparedCurrentValue, param.defaultValue)}
              {renderValue(param.originalValue, param.defaultValue)}
            </>
          ) : (
            <>
              {isOctView &&
                renderValue(param.targetValue, param.defaultValue, true)}
              {isOctView && renderValue(param.currentValue, param.defaultValue)}
              {renderValue(param.originalValue, param.defaultValue)}
            </>
          )}
        </span>
      </div>
      <div className="Analyse-range-slider-container">
        {shouldShowRange && (
          <ColoredRangeSlider
            min={min}
            max={max}
            value={param.originalValue}
            onValueChange={() => {}}
            mode="gradient"
            ranges={{
              yellow: param.yellowRegions || [],
              green: param.greenRegions || [],
              red: param.redRegions || [],
            }}
            markers={markers}
          />
        )}
      </div>
    </div>
  );
};

export default SppParameter;
